"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var widgets_1 = require("@shared/constants/widgets");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
/**
 * This updates and keeps track of ui pages.
 * All updates to the ui should be done through this service.
 */
function UiPagesManagerFactory(widgetUtils) {
    var _a = (function () {
        /**
         * The visible report in the browser is determined entirely by this array.
         * Avoid exposing references to `pages` or its contents.
         * Instead, expose specific APIs to so that updates are done a controlled manner.
         *
         * Each page keeps references to widgets.
         * The widgets are stored in prActiveReport.
         */
        var pages;
        return {
            resetPages: function () {
                pages = [];
                // we must always have at least one page
                addPage();
            },
            getPages: function () {
                if (!pages) {
                    resetPages();
                }
                return pages;
            }
        };
    })(), resetPages = _a.resetPages, getPages = _a.getPages;
    return {
        getPageBreakWidget: getPageBreakWidget,
        getPivotWidget: getPivotWidget,
        resetPages: resetPages,
        cleanExtraPages: cleanExtraPages,
        removeAllPageBreaks: removeAllPageBreaks,
        addWidgetToPage: addWidgetToPage,
        removeWidget: removeWidget,
        addShadow: addShadow,
        removeShadow: removeShadow,
        /**
         * avoid using `getPages` since it exposes a reference
         * to the state of the entire ui. Prefer exposing APIs
         * that read/update the state in a precise manner.
         */
        getPages: getPages
    };
    /************************************
     *          PUBLIC METHODS          *
     ************************************/
    function getPageBreakWidget(pageIndex) {
        var page = getPage(pageIndex);
        // @ts-ignore
        return lodash_1.default.get(page, 'breakWidget');
    }
    /**
     * When inserting a widget at the given `pageIndex`,
     * this returns the widget to insert after.
     */
    function getPivotWidget(pageIndex) {
        if (!lodash_1.default.isNumber(pageIndex) || pageIndex >= getNumPages()) {
            pageIndex = getNumPages() - 1;
        }
        var page = getPage(pageIndex);
        if (page.widgets && page.widgets.length) {
            return page.widgets[page.widgets.length - 1];
        }
        else if (page.breakWidget) {
            return page.breakWidget;
        }
    }
    /**
     * @todo This function is (probably) buggy.
     * If there are multiple pages and the first page has no widgets,
     * then (I think) it should be removed.
     * But this function does not do that.
     */
    function cleanExtraPages() {
        var pages = getPages();
        for (var i = pages.length; --i;) {
            if (!pages[i].widgets.length && !pages[i].breakWidget) {
                pages.splice(i, 1);
            }
        }
        cleanExtraPageBreaks(pages);
        return pages;
    }
    /**
     * @todo This doesn't remove the first page break.
     * That's probably a bug. This was migrated from
     * `clearPageBreaks()` from old commits of flowingPages
     */
    function removeAllPageBreaks() {
        var pages = getPages();
        for (var i = pages.length; --i;) {
            delete pages[i].breakWidget;
        }
    }
    /**
     * Starting from the given pageIndex, finds the
     * first page that the widget can be added to.
     * Then adds widget to the page and
     * returns the page that the widget was added to.
     */
    function addWidgetToPage(widget, pageIndex) {
        if (isPageBreak(widget)) {
            pageIndex = getPageToAddPageBreak(widget, pageIndex);
        }
        while (pageIndex >= getNumPages()) {
            addPage();
        }
        var page = getPage(pageIndex);
        if (isPageBreak(widget)) {
            page.breakWidget = widget;
        }
        else if (widget.moving) {
            // we're not adding the widget
            // so just return the current page index of the widget
            // @ts-ignore
            // @ts-ignore
            pageIndex = widget.layout.page;
        }
        else if (!isWidgetOnPage(pageIndex, widget)) {
            page.widgets.push(widget);
        }
        return pageIndex;
    }
    /**
     * Attempts to remove the widget
     * and returns a boolean indicating
     * whether the widget was removed.
     */
    function removeWidget(widget) {
        var pageIndex = findPageWithWidget(widget);
        if (pageIndex < 0) {
            return false;
        }
        var page = getPage(pageIndex);
        if (isPageBreak(widget)) {
            delete page.breakWidget;
        }
        else {
            lodash_1.default.pull(page.widgets, widget);
        }
        return true;
    }
    function addShadow(shadow) {
        var page = getPage(shadow.page);
        if (page && page.shadows) {
            page.shadows.push(shadow);
        }
    }
    function removeShadow(shadow) {
        var page = getPage(shadow.page);
        if (page && page.shadows) {
            lodash_1.default.pull(page.shadows, shadow);
        }
    }
    /************************************
     *          PRIVATE HELPERS         *
     ************************************/
    function getPage(index) {
        return getPages()[index];
    }
    function getNumPages() {
        return getPages().length;
    }
    function findPageWithWidget(widget) {
        // @ts-ignore
        // @ts-ignore
        if (isWidgetOnPage(widget.layout.page, widget)) {
            // check widget.layout.page first
            // because that is where the widget is likely to be
            // @ts-ignore
            // @ts-ignore
            return widget.layout.page;
        }
        return lodash_1.default.findIndex(getPages(), function (page, pageIndex) {
            return isWidgetOnPage(pageIndex, widget);
        });
    }
    function isWidgetOnPage(pageIndex, widget) {
        var page = getPage(pageIndex);
        if (!page) {
            return false;
        }
        if (isPageBreak(widget)) {
            // @ts-ignore
            // @ts-ignore
            // @ts-ignore
            return page.breakWidget && page.breakWidget.layout.id === widget.layout.id;
        }
        return lodash_1.default.some(page.widgets, {
            layout: {
                // @ts-ignore
                id: widget.layout.id
            }
        });
    }
    function addPage() {
        getPages().push({
            widgets: [],
            shadows: []
        });
    }
    /**
     * In case pages are not handled correctly - there might be some page breaks
     * that are bad states and need to be deleted.
     *
     * Loop through pages and clean up page breaks that are:
     * already deleted from widgets array,
     * or duplicates.
     */
    function cleanExtraPageBreaks(pages) {
        var breakWidgetCount = getBreakWidgetCount(pages);
        var widgetMap = getWidgetMap();
        for (var i = pages.length - 1; i >= 0; i--) {
            var breakWidget = pages[i].breakWidget;
            var wId = breakWidget && breakWidget._id;
            // check if the breakWidget is in the widgets array
            var noBreakWidgetFound = wId && !widgetMap[wId];
            // check if the breakWidget is a duplicate
            // @ts-ignore
            // @ts-ignore
            var duplicateBreakWidget = breakWidgetCount[wId] && breakWidgetCount[wId] > 1;
            if ((noBreakWidgetFound || duplicateBreakWidget) && !pages[i].widgets.length) {
                pages.splice(i, 1);
                if (duplicateBreakWidget) {
                    // @ts-ignore
                    breakWidgetCount[wId] -= 1;
                }
            }
        }
    }
    /**
     * Loop through all pages and get the number of breakWidgets by widget id
     */
    function getBreakWidgetCount(pages) {
        var breakWidgetCount = {};
        lodash_1.default.forEach(pages, function (page) {
            var breakWidget = page.breakWidget;
            if (breakWidget && breakWidget._id) {
                var wId = breakWidget._id;
                // @ts-ignore
                if (!breakWidgetCount[wId]) {
                    // @ts-ignore
                    breakWidgetCount[wId] = 1;
                }
                else {
                    // @ts-ignore
                    breakWidgetCount[wId] += 1;
                }
            }
        });
        return breakWidgetCount;
    }
    function getWidgetMap() {
        var widgets = widgetUtils.getWidgets();
        var map = {};
        lodash_1.default.forEach(widgets, function (widget) {
            // @ts-ignore
            map[widget._id] = true;
        });
        return map;
    }
    function getPageToAddPageBreak(pageBreakWidget, pageIndex) {
        var page = getPage(pageIndex);
        while (page && page.breakWidget && page.breakWidget !== pageBreakWidget) {
            page = getPage(++pageIndex);
        }
        return pageIndex;
    }
    function isPageBreak(widget) {
        return widget._type === widgets_1.PR_WIDGETS.PAGE_BREAK;
    }
}
(function (angularModule) {
    angularModule.factory('pr.uiPagesManager', [
        'pr.widgetUtils',
        UiPagesManagerFactory
    ]);
})(angular_1.module('qualtrics.pr'));
